import React, { useCallback, useState } from 'react';
import { useUser } from '../UserContext';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import MoaniDisplay, { LargeMoaniDisplay } from './MoaniDisplay';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button
} from '@mui/material';
import {
  TrendingUp,
  SwapHoriz,
  MonetizationOn,
  SportsEsports,
  Store,
  EmojiEvents
} from '@mui/icons-material';

function MoaniActivity({ userId }) {
  const navigate = useNavigate();
  const { selectUserById } = useUser();
  const [selectedType, setSelectedType] = useState('');

  // Get user data for display
  const { data: userData } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      if (!userId) return null;
      const response = await api.get(`/users/${userId}`);
      return response.data;
    },
    enabled: !!userId,
    staleTime: 60000 
  });

  // Query for available transaction types
  const { data: availableTypes = [] } = useQuery({
    queryKey: ['moaniTypes', userId],
    queryFn: async () => {
      if (!userId) return [];
      const response = await api.get(`/users/${userId}/moani-types`);
      return response.data;
    },
    enabled: !!userId,
    staleTime: 300000 // 5 minutes
  });

  // Infinite query for MOANI transactions
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: ['moaniActivity', userId, selectedType],
    queryFn: async ({ pageParam = 1 }) => {
      if (!userId) return null;
      const typeParam = selectedType ? `&type=${encodeURIComponent(selectedType)}` : '';
      const response = await api.get(`/users/${userId}/moani-activity?page=${pageParam}&limit=100${typeParam}`);
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.pagination?.hasMore ? lastPage.pagination.page + 1 : undefined;
    },
    enabled: !!userId,
    staleTime: 60000 
  });

  // Flatten all transactions from all pages
  const transactions = data?.pages.flatMap(page => page?.transactions || []) || [];
  const summary = data?.pages?.[0]?.summary || null;

  // Scroll detection for infinite loading
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const threshold = 200; // Load more when 200px from bottom
    
    if (scrollHeight - scrollTop <= clientHeight + threshold && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (!userId) {
    return (
      <Box display="flex" justifyContent="center" mt={4} sx={{ p: 2 }}>
        <Typography variant="h6">No user ID provided.</Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography>Loading MOANI activity...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load MOANI activity: {error.message}
      </Alert>
    );
  }

  const formatMoani = (amount) => {
    if (!amount) return '0.00';
    return (Number(amount) / 1e18).toLocaleString(undefined, { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getTransactionIcon = (type) => {
    const typeStr = type?.toLowerCase() || '';
    if (typeStr.includes('reward') || typeStr.includes('checkin') || typeStr.includes('airdrop')) {
      return <EmojiEvents />;
    } else if (typeStr.includes('auction') || typeStr.includes('vendor') || typeStr.includes('purchase')) {
      return <Store />;
    } else if (typeStr.includes('game') || typeStr.includes('mission')) {
      return <SportsEsports />;
    } else if (typeStr.includes('refund')) {
      return <TrendingUp />;
    } else {
      return <SwapHoriz />;
    }
  };

  const isPositiveTransaction = (amount) => {
    return Number(amount) > 0;
  };

  // Color map for reward types
  const typeColorMap = {
    auctionhousesell: 'primary',
    auctionhousebuy: 'info',
    dailycheckin: 'warning',
    airdrop: 'secondary',
    passreward: 'success',
    deliverymissioncomplete: 'success',
    refund: 'success',
    vendorbuy: 'error',
    moanipurchase: 'error',
    auctionhousefee: 'error',
    // Add more as needed
  };

  const getTypeColor = (type) => {
    if (!type) return 'default';
    const key = type.toLowerCase();
    return typeColorMap[key] || 'default';
  };

  const getSubTypeColor = (subType) => {
    if (!subType) return 'default';
    if (subType.toLowerCase() === 'add') return 'success';
    if (subType.toLowerCase() === 'deduct') return 'error';
    return 'default';
  };

  // Remove related user functionality since MoaniTransactionLogEntries doesn't have this data

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        MOANI Activity - <Typography 
          component={userId ? 'a' : 'span'}
          href={userId ? `/user/${userId}/details` : undefined}
          variant="inherit"
          sx={{ 
            cursor: userId ? 'pointer' : 'default',
            color: userId ? 'primary.main' : 'inherit',
            textDecoration: 'none',
            '&:hover': userId ? {
              color: 'primary.dark',
              textDecoration: 'underline'
            } : {}
          }}
          onClick={(event) => userId && selectUserById(userId, navigate)}
          title={userId ? 'Click to view user details (Ctrl+click for new tab)' : ''}
        >
          {userData?.name || 'User'}
        </Typography>
      </Typography>

      {/* Summary Card */}
      <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2}>
            <MonetizationOn sx={{ fontSize: 40, color: '#fff' }} />
            <Box>
              <Typography color="text.secondary" variant="subtitle2">
                Current MOANI Balance
              </Typography>
              <LargeMoaniDisplay 
                amount={userData?.moani || 0} 
                isWei={true}
                sx={{ color: 'white' }}
              />
              <Typography variant="body2" color="rgba(255,255,255,0.7)">
                Total Transactions: {summary?.totalTransactions || transactions.length}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Filter Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Type</InputLabel>
            <Select
              value={selectedType}
              label="Filter by Type"
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <MenuItem value="">
                <em>All Types</em>
              </MenuItem>
              {availableTypes.map((typeObj) => (
                <MenuItem key={typeObj.type} value={typeObj.type}>
                  {typeObj.type} ({typeObj.count})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedType && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setSelectedType('')}
            >
              Clear Filter
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Transaction History */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {selectedType ? `${selectedType} Transactions` : 'All MOANI Transactions'}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {transactions.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {selectedType ? `No ${selectedType} transactions found` : 'No MOANI transactions found'}
          </Typography>
        ) : (
          <Box
            sx={{
              maxHeight: '60vh',
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
            onScroll={handleScroll}
          >
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Sub Type</strong></TableCell>
                    <TableCell><strong>Amount</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Reference</strong></TableCell>
                    <TableCell><strong>Wallet</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(transaction.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getTransactionIcon(transaction.type)}
                          label={transaction.type || 'Unknown'}
                          size="small"
                          color={getTypeColor(transaction.type)}
                          variant="filled"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.subType || 'N/A'}
                          size="small"
                          color={getSubTypeColor(transaction.subType)}
                          variant="filled"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{
                            fontFamily: 'monospace',
                            color: getSubTypeColor(transaction.subType) === 'success' ? 'success.main' : getSubTypeColor(transaction.subType) === 'error' ? 'error.main' : 'text.primary',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {isPositiveTransaction(transaction.amount) && (
                              <Typography variant="body2" sx={{ mr: 0.5 }}>+</Typography>
                            )}
                            <MoaniDisplay 
                              amount={transaction.amount} 
                              isWei={true}
                              variant="inherit" 
                              component="span"
                            />
                          </Box>
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {transaction.description || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {transaction.referenceId || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {transaction.wallet ? 
                            `${transaction.wallet.slice(0, 6)}...${transaction.wallet.slice(-4)}` : 
                            'N/A'
                          }
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {/* Loading indicator for fetching more pages */}
                  {isFetchingNextPage && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                          <CircularProgress size={24} />
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            Loading more transactions...
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  
                  {/* End of results indicator */}
                  {!hasNextPage && transactions.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Box display="flex" justifyContent="center" py={1}>
                          <Typography variant="body2" color="text.secondary">
                            No more transactions to load
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default MoaniActivity; 